// Event Status Calculation Utility
// Calculates dynamic event status based on enrollment and service windows

import { SecretEvent } from '../supabase'

export type CalculatedEventStatus =
  | 'Draft'
  | 'Scheduled'
  | 'Active'
  | 'Enrollment Closed'
  | 'In Progress'
  | 'Completed'
  | 'Cancelled'

/**
 * Helper function to safely parse dates and handle invalid date strings
 * Returns null for invalid dates instead of Invalid Date object
 */
const parseDate = (dateStr?: string | null): Date | null => {
  if (!dateStr || dateStr.trim() === '') return null
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) {
    console.error(`[getEventStatus] Invalid date string: ${dateStr}`)
    return null
  }
  return date
}

/**
 * Calculate the current status of an event based on time windows
 *
 * Status Priority:
 * 1. Manual statuses (Draft, Cancelled) take precedence
 * 2. In Progress: Service/pickup is currently happening
 * 3. Active: Registration window is currently open
 * 4. Scheduled: Registration hasn't opened yet
 * 5. Enrollment Closed: Between registration close and service start
 * 6. Completed: Service has ended
 */
export const getEventStatus = (event: SecretEvent): CalculatedEventStatus => {
  // Get current time in local timezone (not UTC)
  const now = new Date()

  // Parse event dates using safe date parser
  const registrationOpens = parseDate(event.registration_opens)
  const registrationCloses = parseDate(event.registration_closes)
  const pickupStart = parseDate(event.pickup_start_time)
  const pickupEnd = parseDate(event.pickup_end_time)

  // Validate required dates exist
  if (!registrationOpens || !registrationCloses) {
    console.error('[getEventStatus] Missing required dates', {
      eventId: event.id,
      registrationOpens: event.registration_opens,
      registrationCloses: event.registration_closes
    })
    return 'Draft'
  }

  // Validate date ordering
  if (registrationCloses < registrationOpens) {
    console.error('[getEventStatus] Invalid: registrationCloses before registrationOpens', {
      eventId: event.id,
      opens: registrationOpens.toISOString(),
      closes: registrationCloses.toISOString()
    })
    return 'Draft'
  }

  if (pickupStart && pickupEnd && pickupEnd < pickupStart) {
    console.error('[getEventStatus] Invalid: pickupEnd before pickupStart', {
      eventId: event.id,
      start: pickupStart.toISOString(),
      end: pickupEnd.toISOString()
    })
    return 'Draft'
  }

  // DEBUG: Log all date values
  console.log('[getEventStatus] Event:', event.title)
  console.log('[getEventStatus] Now (local):', now.toLocaleString(), '(', now.getTime(), ')')
  console.log('[getEventStatus] Registration Opens:', registrationOpens.toLocaleString(), '(', registrationOpens.getTime(), ')')
  console.log('[getEventStatus] Registration Closes:', registrationCloses.toLocaleString(), '(', registrationCloses.getTime(), ')')
  console.log('[getEventStatus] Pickup Start:', pickupStart?.toLocaleString(), '(', pickupStart?.getTime(), ')')
  console.log('[getEventStatus] Pickup End:', pickupEnd?.toLocaleString(), '(', pickupEnd?.getTime(), ')')

  // Manual statuses take precedence
  if (event.status === 'cancelled') {
    console.log('[getEventStatus] Result: Cancelled (manual status)')
    return 'Cancelled'
  }
  if (event.status === 'draft') {
    console.log('[getEventStatus] Result: Draft (manual status)')
    return 'Draft'
  }

  // In Progress: Service is happening now (check BEFORE Active to handle overlapping windows)
  if (pickupStart && pickupEnd && now >= pickupStart && now <= pickupEnd) {
    console.log('[getEventStatus] Result: In Progress (pickup window)')
    return 'In Progress'
  }

  // Active: Enrollment is OPEN
  if (now >= registrationOpens && now <= registrationCloses) {
    console.log('[getEventStatus] Result: Active (in registration window)')
    return 'Active'
  }

  // Scheduled: Enrollment hasn't started yet
  if (now < registrationOpens) {
    console.log('[getEventStatus] Result: Scheduled (before registration)')
    return 'Scheduled'
  }

  // Enrollment Closed: Gap between registration and service
  if (pickupStart && now > registrationCloses && now < pickupStart) {
    console.log('[getEventStatus] Result: Enrollment Closed (between registration and pickup)')
    return 'Enrollment Closed'
  }

  // Enrollment Closed (fallback): Handle missing pickup_start but has pickup_end
  if (!pickupStart && pickupEnd && now > registrationCloses && now < pickupEnd) {
    console.log('[getEventStatus] Result: Enrollment Closed (registration closed, before pickup end)')
    return 'Enrollment Closed'
  }

  // Completed: Service has ended
  if (pickupEnd && now > pickupEnd) {
    console.log('[getEventStatus] Result: Completed (after pickup end)')
    return 'Completed'
  }

  // Completed (fallback): If pickup start exists but no end time, assume event duration from registration
  // Once we're at or past the pickup start time and registration has closed, mark as completed
  if (pickupStart && !pickupEnd && now >= pickupStart && now > registrationCloses) {
    console.log('[getEventStatus] Result: Completed (past pickup start, no end time set)')
    return 'Completed'
  }

  // Completed (fallback): If no pickup times set at all, check if registration has closed
  // This handles legacy events or events where pickup times weren't configured
  if (!pickupStart && !pickupEnd && now > registrationCloses) {
    console.log('[getEventStatus] Result: Completed (registration closed, no pickup times)')
    return 'Completed'
  }

  // Default fallback
  console.log('[getEventStatus] Result: Scheduled (fallback)')
  return 'Scheduled'
}

/**
 * Get CSS classes for status badge display
 */
export const getStatusColor = (status: CalculatedEventStatus): string => {
  switch (status) {
    case 'Draft':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
    case 'Scheduled':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
    case 'Active':
      return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
    case 'Enrollment Closed':
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
    case 'In Progress':
      return 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
    case 'Completed':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
    case 'Cancelled':
      return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
  }
}

/**
 * Check if an event should be visible to customers
 * Events are visible if they are Active or Scheduled (upcoming)
 */
export const isEventVisibleToCustomers = (event: SecretEvent): boolean => {
  const status = getEventStatus(event)
  return status === 'Active' || status === 'Scheduled'
}

/**
 * Check if customers can currently place orders for an event
 */
export const canPlaceOrders = (event: SecretEvent): boolean => {
  return getEventStatus(event) === 'Active'
}
